import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import CookieService from "../../context/cookies";

import { base } from "../../constants/apis";

export interface AiInstructionsRes {
  status: string;
  count: number;
  data: Record[];
}

export interface Record {
  id: number;
  uid: number;
  table_id: number;
  row_id?: string;
  instructions: string;
  is_completed: 0 | 1;
  step: string;
  created_at: string;
}

export const aiAutomateApi = createApi({
  reducerPath: "aiAutomateApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis/ai_task`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("jwtToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["aiAutomateApi"],
  endpoints: (builder) => ({
    getAiInstructions: builder.query<
      AiInstructionsRes,
      { table_id: number; row_id?: string }
    >({
      query: (payload) => ({ url: "/get.php", method: "POST", body: payload }),
      providesTags: ["aiAutomateApi"],
    }),

    addAiInstructions: builder.mutation({
      query: (payload) => ({
        url: "/add.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["aiAutomateApi"],

      onQueryStarted: async (payload, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          aiAutomateApi.util.updateQueryData(
            "getAiInstructions",
            { table_id: payload.table_id, row_id: payload.row_id },
            (draft) => {
              draft.data.push({
                ...payload,
                id: crypto.randomUUID(),
              });
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    updateAiInstructions: builder.mutation({
      query: (payload) => ({
        url: "/update.php",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["aiAutomateApi"],
    }),

    deleteAiInstructions: builder.mutation<
      void,
      { ai_task_id: number; table_id: number; row_id?: string }
    >({
      query: ({ ai_task_id }) => ({
        url: "/delete.php",
        method: "POST",
        body: { ai_task_id },
      }),

      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const { table_id, row_id, ai_task_id } = payload;

        // Optimistic update
        const patch = dispatch(
          aiAutomateApi.util.updateQueryData(
            "getAiInstructions",
            { table_id, row_id },
            (draft) => {
              draft.data = draft.data.filter((item) => item.id !== ai_task_id);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },

      invalidatesTags: ["aiAutomateApi"],
    }),
  }),
});

export const {
  useGetAiInstructionsQuery,
  useAddAiInstructionsMutation,
  useUpdateAiInstructionsMutation,
  useDeleteAiInstructionsMutation,
} = aiAutomateApi;
